import axios from 'axios';
import { api } from '../../../server/api';
import {
  ICashMePendingItem,
  ICashMeProposalResult,
  ICashMeLimiters,
  ICashMeSimulationResult,
  ICreateCashMeProposal,
  ICreateCashMeSimulation,
} from './types';

const GENERIC_ERROR =
  'Ocorreu um erro ao enviar seus dados. Por favor, tente novamente mais tarde.';

const UNAVAILABLE_ERROR =
  'O serviço do parceiro está indisponível no momento. Tente novamente em instantes.';

const REVIEW_DATA_ERROR =
  'Não foi possível concluir com os dados informados. Revise as informações e tente novamente.';

/* Backend and CashMe messages arrive in English, so the borrower only ever
   sees Portuguese: known codes and patterns first, then a fallback by status.
   The raw message is kept on `detail` for debugging. */
const MESSAGES_BY_CODE: Record<string, string> = {
  INVALID_EMAIL:
    'Não conseguimos validar este e-mail. Confira se ele está correto.',
  CDI_NOT_ALLOWED: 'O índice CDI não está disponível no momento.',
  SIMULATION_PARAMETERS_NOT_ALLOWED:
    'Essa combinação de tabela, correção e índice não está disponível. Escolha outra condição e simule novamente.',
};

const MESSAGES_BY_PATTERN: [RegExp, string][] = [
  [
    /simulation parameters .* not allowed/i,
    'Essa combinação de tabela, correção e índice não está disponível. Escolha outra condição e simule novamente.',
  ],
  [
    /cannot be used for a new proposal/i,
    'Este documento não pode ser usado em uma nova proposta.',
  ],
];

function toPendingItems(raw: unknown): ICashMePendingItem[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry): ICashMePendingItem => {
      if (typeof entry === 'string') {
        return { code: '', level: 'ERROR', message: entry };
      }

      const item = (entry ?? {}) as Record<string, any>;
      const message =
        item.message ??
        item.description ??
        item.detail ??
        item.reason ??
        item.title ??
        item.field;

      return {
        code: item.code ?? '',
        level: item.level === 'WARNING' ? 'WARNING' : 'ERROR',
        message: typeof message === 'string' ? message : undefined,
        context: item.context,
      };
    })
    .filter((item) => item.message || item.code);
}

function toFriendlyMessage(
  rawMessage: string | undefined,
  status?: number,
  code?: string
): string {
  if (code && MESSAGES_BY_CODE[code]) return MESSAGES_BY_CODE[code];

  const match = MESSAGES_BY_PATTERN.find(([pattern]) =>
    pattern.test(rawMessage ?? '')
  );
  if (match) return match[1];

  if (!status || status >= 500) return UNAVAILABLE_ERROR;
  if (status === 400) return REVIEW_DATA_ERROR;

  return GENERIC_ERROR;
}

export class CashMeError extends Error {
  status?: number;
  code?: string;
  pendingItems?: ICashMePendingItem[];
  detail?: string;

  constructor(
    message: string,
    status?: number,
    code?: string,
    pendingItems?: ICashMePendingItem[],
    detail?: string
  ) {
    super(message);
    this.name = 'CashMeError';
    this.status = status;
    this.code = code;
    this.pendingItems = pendingItems;
    this.detail = detail;
  }

  /** 409 means the lead already has a proposal — retrying never succeeds. */
  get isDuplicate(): boolean {
    return this.status === 409;
  }
}

function toCashMeError(error: unknown): CashMeError {
  if (error instanceof CashMeError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    const status = error.response?.status;
    const code = data?.body?.code;
    console.log('CashMe error:', status, code, data?.message, data?.body);

    return new CashMeError(
      toFriendlyMessage(data?.message, status, code),
      status,
      code,
      toPendingItems(data?.body?.pendingItems),
      data?.message
    );
  }

  console.log('CashMe error:', error);
  return new CashMeError(GENERIC_ERROR);
}

export async function getCashMeLimiters(): Promise<ICashMeLimiters> {
  try {
    const response = await api.get('/cashme/limiters');

    if (response.data.error || !response.data.body) {
      throw new CashMeError(UNAVAILABLE_ERROR, 502);
    }

    return response.data.body as ICashMeLimiters;
  } catch (error) {
    throw toCashMeError(error);
  }
}

export async function createCashMeSimulation(
  payload: ICreateCashMeSimulation
): Promise<ICashMeSimulationResult> {
  try {
    const response = await api.post('/cashme/simulations', payload);

    if (response.data.error) {
      throw new CashMeError(
        toFriendlyMessage(response.data.message, 400, response.data.body?.code),
        400,
        response.data.body?.code,
        toPendingItems(response.data.body?.pendingItems),
        response.data.message
      );
    }

    return response.data.body as ICashMeSimulationResult;
  } catch (error) {
    throw toCashMeError(error);
  }
}

export async function createCashMeProposal(
  payload: ICreateCashMeProposal
): Promise<ICashMeProposalResult> {
  try {
    const response = await api.post('/cashme/proposals', payload);

    if (response.data.error) {
      throw new CashMeError(
        toFriendlyMessage(response.data.message, 400, response.data.body?.code),
        400,
        response.data.body?.code,
        toPendingItems(response.data.body?.pendingItems),
        response.data.message
      );
    }

    return response.data.body as ICashMeProposalResult;
  } catch (error) {
    throw toCashMeError(error);
  }
}
